// Internationalization (i18n) support for Polish and English

export const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      dashboard: "Dashboard",
      settings: "Settings",
      logout: "Logout",
      login: "Login",
      register: "Register",
      liveTranslation: "Live Translation"
    },
    // Landing page
    landing: {
      hero: {
        title: "Stay in the conversation.",
        subtitle: "Never miss a detail.",
        description: "AI meeting notes that capture every word while you stay fully engaged. No bot interruptions. No manual note-taking. Just accurate summaries delivered automatically.",
        cta: "Start Now",
        ctaSecondary: "Learn More"
      },
      features: {
        title: "Stop choosing between taking notes and being present",
        description: "ExpoTalk runs in the background during any meeting capturing everything with advanced AI. Get instant summaries, searchable transcripts, and automatic action items.",
        presence: {
          title: "Full Presence",
          description: "Participate actively in meetings without worrying about capturing details"
        },
        friction: {
          title: "Zero Friction",
          description: "No bots to invite, no recording warnings, no awkward setup"
        },
        clarity: {
          title: "Instant Clarity",
          description: "Walk out of every meeting with organized summaries and clear next steps"
        }
      },
      howItWorks: {
        title: "How It Works",
        step1: {
          title: "Start Recording",
          description: "Launch the app and start recording with one click"
        },
        step2: {
          title: "Stay Present",
          description: "Participate without distraction in your meeting"
        },
        step3: {
          title: "Get Results",
          description: "Receive transcripts, summaries, and action items instantly"
        }
      },
      useCases: {
        title: "Why Choose ExpoTalk",
        client: {
          title: "Client Calls",
          description: "Stay fully present with clients while capturing every commitment"
        },
        research: {
          title: "Research Interviews",
          description: "Focus on your subjects while capturing every nuance"
        },
        team: {
          title: "Team Meetings",
          description: "Keep your team aligned with accurate notes and action items"
        }
      },
      cta: {
        title: "Ready to Transform Your Meetings?",
        description: "Join professionals who trust ExpoTalk to capture every important moment.",
        button: "Get Started Free"
      }
    },
    // Auth
    auth: {
      login: {
        title: "Welcome back",
        subtitle: "Sign in to your account",
        email: "Email",
        password: "Password",
        submit: "Sign In",
        noAccount: "Don't have an account?",
        register: "Register"
      },
      register: {
        title: "Create account",
        subtitle: "Get started with ExpoTalk",
        name: "Full Name",
        email: "Email",
        password: "Password",
        submit: "Create Account",
        hasAccount: "Already have an account?",
        login: "Sign In"
      }
    },
    // Dashboard
    dashboard: {
      title: "Your Meetings",
      subtitle: "Manage and review your meeting recordings and live sessions",
      newMeeting: "New Recording",
      search: "Search meetings and sessions...",
      noMeetings: "No meetings yet",
      noMeetingsDesc: "Start your first recording to see it here",
      createDemo: "Create Demo Meeting",
      status: {
        pending: "Pending",
        processing: "Processing",
        completed: "Completed",
        active: "Active",
        error: "Error"
      },
      card: {
        actionItems: "action items",
        viewDetails: "View Details"
      },
      confirmDelete: "Are you sure you want to delete this?",
      delete: "Delete",
      deleteSuccess: "Deleted successfully",
      deleteError: "Failed to delete",
      tabs: {
        all: "All",
        meetings: "Meetings",
        live: "Live Sessions"
      }
    },
    // Meeting Detail
    meeting: {
      backToDashboard: "Back to Dashboard",
      tabs: {
        transcript: "Transcript",
        summary: "Summary",
        actions: "Action Items",
        chat: "AI Chat"
      },
      transcript: {
        title: "Full Transcript",
        noTranscript: "No transcript available yet",
        searchPlaceholder: "Search in transcript..."
      },
      summary: {
        title: "Meeting Summary",
        noSummary: "No summary available yet",
        keyTopics: "Key Topics",
        sentiment: "Sentiment"
      },
      actions: {
        title: "Action Items",
        noActions: "No action items found",
        completed: "completed",
        pending: "pending"
      },
      chat: {
        title: "Ask About This Meeting",
        placeholder: "Ask a question about this meeting...",
        send: "Send",
        thinking: "Thinking..."
      }
    },
    // Recording
    recording: {
      title: "New Recording",
      subtitle: "Record your meeting and let AI do the rest",
      meetingTitle: "Meeting Title",
      meetingTitlePlaceholder: "e.g., Weekly Team Sync",
      description: "Description (optional)",
      descriptionPlaceholder: "Brief description of the meeting",
      language: "Language",
      startRecording: "Start Recording",
      stopRecording: "Stop Recording",
      recording: "Recording...",
      processing: "Processing your recording...",
      upload: "Upload Audio",
      uploadDesc: "Or upload an existing audio file",
      supportedFormats: "Supported formats: MP3, WAV, WebM, M4A",
      liveAnalysis: "Live Analysis",
      liveAnalysisDesc: "AI transcription with speaker identification every 30s",
      enterTitle: "Please enter a meeting title first",
      started: "Recording started",
      analyzing: "Analyzing...",
      nextAnalysis: "Next analysis in",
      firstAnalysisIn: "First analysis in",
      analyzingFirstChunk: "Analyzing first audio segment...",
      liveTranscript: "Live Transcript",
      transcriptWillAppear: "Transcript will appear here during recording...",
      speakersDetected: "Speakers detected",
      topicsDetected: "Topics",
      status: {
        ready: "Ready to record",
        recording: "Recording in progress",
        stopped: "Recording stopped",
        uploading: "Uploading...",
        processing: "Processing with AI..."
      }
    },
    // Settings
    settings: {
      title: "Settings",
      subtitle: "Manage your preferences",
      appearance: {
        title: "Appearance",
        theme: "Theme",
        light: "Light",
        dark: "Dark",
        system: "System"
      },
      language: {
        title: "Language",
        description: "Choose your preferred language"
      },
      account: {
        title: "Account",
        email: "Email",
        name: "Name",
        memberSince: "Member since"
      }
    },
    // Search
    search: {
      title: "Search Results",
      placeholder: "Search your meetings...",
      noResults: "No results found",
      noResultsDesc: "Try adjusting your search query",
      resultsCount: "results found"
    },
    // Admin settings (superadmin)
    adminSettings: {
      title: "Settings",
      subtitle: "ExpoTalk configuration — stored in ExpoTalk, managed only here",
      save: "Save changes",
      saved: "Settings saved",
      saveFailed: "Failed to save settings",
      loadFailed: "Failed to load settings",
      denied: "This page is only accessible to superadmins.",
      notSet: "not set — feature returns an error",
      secretSet: "set (hidden)",
      modified: "modified",
      clear: "Clear",
      placeholderSet: "set — type to replace",
      placeholderNotSet: "not set",
      invalidNumber: "Invalid number",
      groups: {
        transcription: ["Transcription", "Transcription provider, its key and the daily limit (provider cost)"],
        security: ["Security", "Allowed browser origins (CORS), comma separated. Registration stays closed — accounts are created by an administrator."],
        models: ["AI models", "Models for the other processing stages"],
        limits: ["Limits", "Upload, list and search limits"],
        processing: ["Processing", "Audio processing and retry parameters"],
        live: ["Live defaults", "Default values for live translation sessions"],
        summary: ["Summary thresholds", "Character thresholds for auto-scaling summary detail"],
        integrations: ["Integrations", "External services used by ExpoTalk"]
      },
      labels: {
        "processing.transcription_provider": "Transcription provider",
        "llm.gemini_api_key": "Gemini API key",
        "processing.assemblyai_api_key": "AssemblyAI API key",
        "ai.transcription_model": "Transcription model",
        "ai.summary_model": "Summary model",
        "limits.daily_transcriptions": "Daily transcription limit",
        "security.cors_origins": "Allowed origins (CORS)"
      },
      apiKeys: {
        title: "Integration API keys",
        description: "For applications that use ExpoTalk (e.g. Radar). Each key belongs to one organization and only sees its recordings. The full key is shown once.",
        copyNow: "Copy this key now — it will not be shown again:",
        name: "Name",
        organization: "Organization ID",
        create: "Create key",
        none: "No API keys.",
        lastUsed: "last used",
        revoked: "revoked",
        revoke: "Revoke",
        revokedOk: "API key revoked",
        failed: "API key operation failed"
      }
    },
    // Common
    common: {
      loading: "Loading...",
      error: "An error occurred",
      retry: "Retry",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      close: "Close",
      confirm: "Confirm"
    }
  },
  pl: {
    // Navigation
    nav: {
      home: "Strona główna",
      dashboard: "Panel",
      settings: "Ustawienia",
      logout: "Wyloguj",
      login: "Zaloguj",
      register: "Zarejestruj",
      liveTranslation: "Tłumaczenie na żywo"
    },
    // Landing page
    landing: {
      hero: {
        title: "Bądź obecny w rozmowie.",
        subtitle: "Nie przegap żadnego szczegółu.",
        description: "Notatki ze spotkań z AI, które zapisują każde słowo, gdy Ty możesz się w pełni zaangażować. Bez botów. Bez ręcznego notowania. Tylko dokładne podsumowania dostarczane automatycznie.",
        cta: "Rozpocznij",
        ctaSecondary: "Dowiedz się więcej"
      },
      features: {
        title: "Przestań wybierać między robieniem notatek a byciem obecnym",
        description: "ExpoTalk działa w tle podczas każdego spotkania, rejestrując wszystko za pomocą zaawansowanej AI. Otrzymuj natychmiastowe podsumowania, przeszukiwalne transkrypcje i automatyczne punkty działania.",
        presence: {
          title: "Pełna obecność",
          description: "Aktywnie uczestniczysz w spotkaniach bez martwienia się o zapisywanie szczegółów"
        },
        friction: {
          title: "Zero tarcia",
          description: "Bez botów do zapraszania, bez ostrzeżeń o nagrywaniu, bez niezręcznej konfiguracji"
        },
        clarity: {
          title: "Natychmiastowa jasność",
          description: "Wychodź z każdego spotkania z uporządkowanymi podsumowaniami i jasnymi kolejnymi krokami"
        }
      },
      howItWorks: {
        title: "Jak to działa",
        step1: {
          title: "Rozpocznij nagrywanie",
          description: "Uruchom aplikację i zacznij nagrywać jednym kliknięciem"
        },
        step2: {
          title: "Bądź obecny",
          description: "Uczestniczysz bez rozpraszania się w swoim spotkaniu"
        },
        step3: {
          title: "Otrzymaj wyniki",
          description: "Otrzymaj transkrypcje, podsumowania i punkty działania natychmiast"
        }
      },
      useCases: {
        title: "Dlaczego warto wybrać ExpoTalk",
        client: {
          title: "Rozmowy z klientami",
          description: "Bądź w pełni obecny z klientami, rejestrując każde zobowiązanie"
        },
        research: {
          title: "Wywiady badawcze",
          description: "Skup się na rozmówcach, rejestrując każdy niuans"
        },
        team: {
          title: "Spotkania zespołu",
          description: "Utrzymuj zespół na bieżąco z dokładnymi notatkami i punktami działania"
        }
      },
      cta: {
        title: "Gotowy na transformację swoich spotkań?",
        description: "Dołącz do profesjonalistów, którzy ufają ExpoTalk w rejestrowaniu każdej ważnej chwili.",
        button: "Rozpocznij za darmo"
      }
    },
    // Auth
    auth: {
      login: {
        title: "Witaj ponownie",
        subtitle: "Zaloguj się do swojego konta",
        email: "Email",
        password: "Hasło",
        submit: "Zaloguj się",
        noAccount: "Nie masz konta?",
        register: "Zarejestruj się"
      },
      register: {
        title: "Utwórz konto",
        subtitle: "Rozpocznij z ExpoTalk",
        name: "Imię i nazwisko",
        email: "Email",
        password: "Hasło",
        submit: "Utwórz konto",
        hasAccount: "Masz już konto?",
        login: "Zaloguj się"
      }
    },
    // Dashboard
    dashboard: {
      title: "Twoje spotkania",
      subtitle: "Zarządzaj i przeglądaj swoje nagrania ze spotkań i sesje live",
      newMeeting: "Nowe nagranie",
      search: "Szukaj spotkań i sesji...",
      noMeetings: "Brak spotkań",
      noMeetingsDesc: "Rozpocznij swoje pierwsze nagranie, aby zobaczyć je tutaj",
      createDemo: "Utwórz demo spotkanie",
      status: {
        pending: "Oczekuje",
        processing: "Przetwarzanie",
        completed: "Ukończone",
        active: "Aktywna",
        error: "Błąd"
      },
      card: {
        actionItems: "punkty działania",
        viewDetails: "Zobacz szczegóły"
      },
      confirmDelete: "Czy na pewno chcesz to usunąć?",
      delete: "Usuń",
      deleteSuccess: "Usunięto pomyślnie",
      deleteError: "Nie udało się usunąć",
      tabs: {
        all: "Wszystko",
        meetings: "Spotkania",
        live: "Sesje Live"
      }
    },
    // Meeting Detail
    meeting: {
      backToDashboard: "Powrót do panelu",
      tabs: {
        transcript: "Transkrypcja",
        summary: "Podsumowanie",
        actions: "Punkty działania",
        chat: "Czat AI"
      },
      transcript: {
        title: "Pełna transkrypcja",
        noTranscript: "Brak dostępnej transkrypcji",
        searchPlaceholder: "Szukaj w transkrypcji..."
      },
      summary: {
        title: "Podsumowanie spotkania",
        noSummary: "Brak dostępnego podsumowania",
        keyTopics: "Kluczowe tematy",
        sentiment: "Nastrój rozmowy"
      },
      actions: {
        title: "Punkty działania",
        noActions: "Nie znaleziono punktów działania",
        completed: "ukończone",
        pending: "oczekujące"
      },
      chat: {
        title: "Zapytaj o to spotkanie",
        placeholder: "Zadaj pytanie o to spotkanie...",
        send: "Wyślij",
        thinking: "Myślę..."
      }
    },
    // Recording
    recording: {
      title: "Nowe nagranie",
      subtitle: "Nagraj spotkanie i pozwól AI zrobić resztę",
      meetingTitle: "Tytuł spotkania",
      meetingTitlePlaceholder: "np. Cotygodniowy sync zespołu",
      description: "Opis (opcjonalnie)",
      descriptionPlaceholder: "Krótki opis spotkania",
      language: "Język",
      startRecording: "Rozpocznij nagrywanie",
      stopRecording: "Zatrzymaj nagrywanie",
      recording: "Nagrywanie...",
      processing: "Przetwarzanie nagrania...",
      upload: "Prześlij audio",
      uploadDesc: "Lub prześlij istniejący plik audio",
      supportedFormats: "Obsługiwane formaty: MP3, WAV, WebM, M4A",
      liveAnalysis: "Analiza na żywo",
      liveAnalysisDesc: "Transkrypcja AI z identyfikacją mówców co 30s",
      enterTitle: "Najpierw wpisz tytuł spotkania",
      started: "Nagrywanie rozpoczęte",
      analyzing: "Analizuję...",
      nextAnalysis: "Następna analiza za",
      firstAnalysisIn: "Pierwsza analiza za",
      analyzingFirstChunk: "Analizuję pierwszy fragment audio...",
      liveTranscript: "Transkrypcja na żywo",
      transcriptWillAppear: "Transkrypcja pojawi się tutaj podczas nagrywania...",
      speakersDetected: "Wykryci mówcy",
      topicsDetected: "Tematy",
      status: {
        ready: "Gotowy do nagrywania",
        recording: "Nagrywanie w toku",
        stopped: "Nagrywanie zatrzymane",
        uploading: "Przesyłanie...",
        processing: "Przetwarzanie przez AI..."
      }
    },
    // Settings
    settings: {
      title: "Ustawienia",
      subtitle: "Zarządzaj swoimi preferencjami",
      appearance: {
        title: "Wygląd",
        theme: "Motyw",
        light: "Jasny",
        dark: "Ciemny",
        system: "Systemowy"
      },
      language: {
        title: "Język",
        description: "Wybierz preferowany język"
      },
      account: {
        title: "Konto",
        email: "Email",
        name: "Imię",
        memberSince: "Członek od"
      }
    },
    // Search
    search: {
      title: "Wyniki wyszukiwania",
      placeholder: "Szukaj w spotkaniach...",
      noResults: "Brak wyników",
      noResultsDesc: "Spróbuj zmienić zapytanie wyszukiwania",
      resultsCount: "znalezionych wyników"
    },
    // Ustawienia administratora (superadmin)
    adminSettings: {
      title: "Ustawienia",
      subtitle: "Konfiguracja ExpoTalk — zapisana w ExpoTalk, zarządzana wyłącznie tutaj",
      save: "Zapisz zmiany",
      saved: "Ustawienia zapisane",
      saveFailed: "Nie udało się zapisać ustawień",
      loadFailed: "Nie udało się wczytać ustawień",
      denied: "Ta strona jest dostępna tylko dla superadministratorów.",
      notSet: "brak — funkcja zgłosi błąd",
      secretSet: "ustawiony (ukryty)",
      modified: "zmieniony",
      clear: "Wyczyść",
      placeholderSet: "ustawiony — wpisz, aby zmienić",
      placeholderNotSet: "brak",
      invalidNumber: "Niepoprawna liczba",
      groups: {
        transcription: ["Transkrypcja", "Dostawca transkrypcji, jego klucz i dzienny limit (koszt dostawcy)"],
        security: ["Bezpieczeństwo", "Dozwolone adresy przeglądarki (CORS), po przecinku. Rejestracja pozostaje zamknięta — konta zakłada administrator."],
        models: ["Modele AI", "Modele dla pozostałych etapów przetwarzania"],
        limits: ["Limity", "Limity wgrywania, list i wyszukiwania"],
        processing: ["Przetwarzanie", "Parametry przetwarzania audio i ponowień"],
        live: ["Sesje na żywo", "Wartości domyślne sesji tłumaczenia na żywo"],
        summary: ["Progi streszczeń", "Progi długości tekstu dla szczegółowości streszczenia"],
        integrations: ["Integracje", "Usługi zewnętrzne używane przez ExpoTalk"]
      },
      labels: {
        "processing.transcription_provider": "Dostawca transkrypcji",
        "llm.gemini_api_key": "Klucz API Gemini",
        "processing.assemblyai_api_key": "Klucz API AssemblyAI",
        "ai.transcription_model": "Model transkrypcji",
        "ai.summary_model": "Model streszczenia",
        "limits.daily_transcriptions": "Dzienny limit transkrypcji",
        "security.cors_origins": "Dozwolone adresy (CORS)"
      },
      apiKeys: {
        title: "Klucze API integracji",
        description: "Dla aplikacji korzystających z ExpoTalk (np. Radar). Każdy klucz należy do jednej organizacji i widzi tylko jej nagrania. Pełny klucz jest pokazywany raz.",
        copyNow: "Skopiuj ten klucz teraz — nie zostanie pokazany ponownie:",
        name: "Nazwa",
        organization: "ID organizacji",
        create: "Utwórz klucz",
        none: "Brak kluczy API.",
        lastUsed: "ostatnio użyty",
        revoked: "unieważniony",
        revoke: "Unieważnij",
        revokedOk: "Klucz API unieważniony",
        failed: "Operacja na kluczu API nie powiodła się"
      }
    },
    // Common
    common: {
      loading: "Ładowanie...",
      error: "Wystąpił błąd",
      retry: "Ponów",
      save: "Zapisz",
      cancel: "Anuluj",
      delete: "Usuń",
      edit: "Edytuj",
      close: "Zamknij",
      confirm: "Potwierdź"
    }
  }
};

export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let result = translations[lang] || translations.en;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      // Fallback to English
      result = translations.en;
      for (const k of keys) {
        if (result && typeof result === 'object' && k in result) {
          result = result[k];
        } else {
          return path; // Return path if translation not found
        }
      }
      break;
    }
  }
  
  return result;
};
